import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getLabCatalog,
  getLabFusionClusters,
  getLabFusionHumanTopPriorities,
  getLabFusionRootCauseSummary,
  getLabFusionRunReport,
  getLabFusionSequentialProfiles,
} from '../api/smartiLabApi';
import LabBilingualCopy from '../components/LabBilingualCopy';
import LabCard from '../components/LabCard';
import LabEmptyState from '../components/LabEmptyState';
import LabFilterBar from '../components/LabFilterBar';
import LabMetric from '../components/LabMetric';
import LabTable from '../components/LabTable';
import SmartiLabBadge from '../components/SmartiLabBadge';
import SmartiLabIcon from '../components/SmartiLabIcon';
import SmartiLabScoreBar from '../components/SmartiLabScoreBar';
import { useSmartiLabContext } from '../SmartiLabContext';
import { getSmartiLabLocaleCopy, smartiLabHelpCopy } from '../content/smartiLabHelpCopy';

function formatList(value, fallback) {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    } catch {
      return [value];
    }
  }
  if (Array.isArray(value) && value.length) return value;
  return [fallback];
}

export default function RootCauseExplorerPage() {
  const { i18n } = useTranslation();
  const { sharedQuery } = useSmartiLabContext();
  const [catalog, setCatalog] = useState(null);
  const [stationName, setStationName] = useState('');
  const [ateVersion, setAteVersion] = useState('');
  const [testspec, setTestspec] = useState('');
  const [machineIdentifier, setMachineIdentifier] = useState('');
  const [search, setSearch] = useState('');
  const [minConfidence, setMinConfidence] = useState('0.45');
  const [minEvidence, setMinEvidence] = useState('0.40');
  const [sequenceRole, setSequenceRole] = useState('');
  const [clusterId, setClusterId] = useState('');
  const [hideAmbiguousBurnLinks, setHideAmbiguousBurnLinks] = useState(true);
  const [strictOnly, setStrictOnly] = useState(true);
  const [summary, setSummary] = useState([]);
  const [humanPriorities, setHumanPriorities] = useState([]);
  const [sequentialProfiles, setSequentialProfiles] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [runReport, setRunReport] = useState(null);
  const [error, setError] = useState('');
  const isItalian = i18n.language !== 'en';

  useEffect(() => {
    getLabCatalog(sharedQuery).then(setCatalog).catch(() => {});
    getLabFusionRunReport().then(setRunReport).catch(() => {});
    getLabFusionClusters().then((data) => setClusters(data.rows || [])).catch(() => {});
  }, [sharedQuery]);

  useEffect(() => {
    const params = {
      ...sharedQuery,
      search,
      stationName,
      testspec,
      machineIdentifier,
      ateVersion,
      minConfidence,
      hideAmbiguousBurnLinks,
      strictOnly,
      limit: 80,
    };

    Promise.all([
      getLabFusionRootCauseSummary(params),
      getLabFusionSequentialProfiles({ search }),
      getLabFusionHumanTopPriorities({
        limit: 12,
        station: stationName,
        machine: machineIdentifier,
        testspec,
        strictOnly,
      }),
    ])
      .then(([summaryData, sequentialData, humanData]) => {
        const filteredRows = (summaryData.rows || []).filter((row) => {
          const evidencePass = row?.investigation_priority == null || Number(row.investigation_priority) >= Number(minEvidence);
          const sequencePass = !sequenceRole || !row?.sequence_role || row.sequence_role === sequenceRole;
          const clusterPass = !clusterId || String(row?.cluster_id || row?.session_cluster_id || '') === clusterId;
          return evidencePass && sequencePass && clusterPass;
        });
        setSummary(filteredRows);
        setSequentialProfiles((sequentialData.rows || []).slice(0, 20));
        setHumanPriorities(humanData?.rows || []);
        setError('');
      })
      .catch((err) => setError(err.message));
  }, [ateVersion, clusterId, hideAmbiguousBurnLinks, machineIdentifier, minConfidence, minEvidence, search, sequenceRole, sharedQuery, stationName, strictOnly, testspec]);

  const noSuspectsCopy = getSmartiLabLocaleCopy(smartiLabHelpCopy.noRootCauseSuspects, i18n.language);
  const topPriority = humanPriorities[0] || null;

  return (
    <div className="lab-page">
      <section className="lab-page-hero">
        <div className="lab-page-hero-layout">
          <div className="lab-page-hero-meta">
            <div className="lab-page-hero-head">
              <span className="lab-page-icon"><SmartiLabIcon /></span>
              <div>
                <span className="lab-badge">{isItalian ? 'Indagine prioritaria' : 'Priority investigation'}</span>
                <h2>{isItalian ? 'Dove indagare prima' : 'Where to investigate first'}</h2>
              </div>
            </div>
            <p className="lab-page-hero-subtitle">
              {topPriority
                ? (isItalian ? topPriority.short_explanation_it : topPriority.short_explanation_en)
                : (isItalian
                  ? 'Qui Smarti Lab traduce i sospetti tecnici in priorità di controllo per utenti non tecnici.'
                  : 'Here Smarti Lab translates technical suspects into check-first priorities for non-technical users.')}
            </p>
            <LabBilingualCopy
              it={topPriority ? `Il sospetto più affidabile adesso è ${topPriority.related_step_name || topPriority.title_it}. Smarti Lab lo mette in alto perché ricorre nei guasti e mostra segnali leggibili.` : 'Questa pagina mostra dove controllare prima. I risultati indicano priorità di indagine, non cause confermate.'}
              en={topPriority ? `The most reliable suspect right now is ${topPriority.related_step_name || topPriority.title_en}. Smarti Lab places it first because it recurs in failures and shows readable signals.` : 'This page shows where to check first. Results indicate investigation priority, not confirmed cause.'}
            />
          </div>
          <div className="lab-page-hero-panel">
            <div className="lab-hero-highlights">
              <div className="lab-hero-highlight">
                <strong>{isItalian ? 'Sospetti attivi' : 'Active suspects'}</strong>
                <p>{summary.length}</p>
              </div>
              <div className="lab-hero-highlight">
                <strong>{isItalian ? 'Strict mode' : 'Strict mode'}</strong>
                <p>{strictOnly ? (isItalian ? 'Attivo' : 'Enabled') : (isItalian ? 'Disattivo' : 'Disabled')}</p>
              </div>
              <div className="lab-hero-highlight">
                <strong>{isItalian ? 'Cluster disponibili' : 'Available clusters'}</strong>
                <p>{clusters.length}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LabCard
        title={isItalian ? 'Priorità umane di indagine' : 'Human investigation priorities'}
        description={<LabBilingualCopy it="Ogni carta spiega cosa controllare prima, perché Smarti Lab lo suggerisce e quale azione fare adesso." en="Each card explains what to check first, why Smarti Lab suggests it, and what action to take now." />}
      >
        {humanPriorities.length ? (
          <div className="lab-human-priority-grid">
            {humanPriorities.map((priority) => (
              <article key={priority.priority_id} className="lab-human-priority-card">
                <div className="lab-human-priority-head">
                  <div className="lab-data-stack">
                    <span className="lab-badge">{priority.priority_rank}</span>
                    <strong>{isItalian ? priority.title_it : priority.title_en}</strong>
                    <p>{isItalian ? priority.short_explanation_it : priority.short_explanation_en}</p>
                  </div>
                  <div className="lab-chip-row">
                    <SmartiLabBadge variant="strict" label={isItalian ? priority.evidence_label_it : priority.evidence_label_en} />
                    <SmartiLabBadge variant="success" label={isItalian ? priority.confidence_label_it : priority.confidence_label_en} />
                  </div>
                </div>
                <div className="lab-human-action-panel">
                  <strong>{isItalian ? 'Perché Smarti Lab lo pensa' : 'Why Smarti Lab thinks this'}</strong>
                  <p>{isItalian ? priority.why_it : priority.why_en}</p>
                </div>
                <div className="lab-human-action-panel">
                  <strong>{isItalian ? 'Cosa fare adesso' : 'What to do next'}</strong>
                  <p>{isItalian ? priority.next_action_it : priority.next_action_en}</p>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <LabEmptyState title={noSuspectsCopy.title} description={noSuspectsCopy.body} />
        )}
      </LabCard>

      <details className="lab-technical-details">
        <summary>{isItalian ? 'Mostra dettagli tecnici' : 'Show technical details'}</summary>
        <div className="lab-technical-stack">
      <LabCard
        title={isItalian ? 'Filtri di indagine' : 'Investigation filters'}
        description={<LabBilingualCopy it="Questa pagina raggruppa i passaggi che ricorrono come sospetti nelle sessioni guaste. Mostra evidenza, confidenza, supporto e motivi principali. Serve a trovare pattern ripetuti, non a dichiarare automaticamente la causa." en="This page groups steps that repeatedly appear as suspects in failed sessions. It shows evidence, confidence, support, and main reasons. It is used to find repeated patterns, not to automatically declare the cause." />}
        help={{ ...smartiLabHelpCopy.rootCauseExplorer, ariaLabel: 'Spiegazione Root Cause Explorer / Root Cause Explorer explanation' }}
      >
        <LabFilterBar>
          <select className="lab-input" value={stationName} onChange={(event) => setStationName(event.target.value)}>
            <option value="">{isItalian ? 'Tutte le stazioni' : 'All stations'}</option>
            {(catalog?.production?.stations || []).map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
          <select className="lab-input" value={testspec} onChange={(event) => setTestspec(event.target.value)}>
            <option value="">{isItalian ? 'Tutti i Testspec' : 'All Testspecs'}</option>
            {(catalog?.production?.testspecs || []).map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
          <select className="lab-input" value={ateVersion} onChange={(event) => setAteVersion(event.target.value)}>
            <option value="">{isItalian ? 'Tutte le versioni ATE' : 'All ATE versions'}</option>
            {(catalog?.production?.ateVersions || []).map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
          <input className="lab-input" placeholder={isItalian ? 'Macchina' : 'Machine'} value={machineIdentifier} onChange={(event) => setMachineIdentifier(event.target.value)} />
          <input className="lab-input" placeholder={isItalian ? 'Cerca step o numero step' : 'Search step or step number'} value={search} onChange={(event) => setSearch(event.target.value)} />
          <select className="lab-input" value={minConfidence} onChange={(event) => setMinConfidence(event.target.value)}>
            <option value="0.25">{isItalian ? 'Confidenza minima 0.25' : 'Minimum confidence 0.25'}</option>
            <option value="0.45">{isItalian ? 'Confidenza minima 0.45' : 'Minimum confidence 0.45'}</option>
            <option value="0.60">{isItalian ? 'Confidenza minima 0.60' : 'Minimum confidence 0.60'}</option>
            <option value="0.75">{isItalian ? 'Confidenza minima 0.75' : 'Minimum confidence 0.75'}</option>
          </select>
          <select className="lab-input" value={minEvidence} onChange={(event) => setMinEvidence(event.target.value)}>
            <option value="0.25">{isItalian ? 'Evidenza minima 0.25' : 'Minimum evidence 0.25'}</option>
            <option value="0.40">{isItalian ? 'Evidenza minima 0.40' : 'Minimum evidence 0.40'}</option>
            <option value="0.60">{isItalian ? 'Evidenza minima 0.60' : 'Minimum evidence 0.60'}</option>
            <option value="0.75">{isItalian ? 'Evidenza minima 0.75' : 'Minimum evidence 0.75'}</option>
          </select>
          <select className="lab-input" value={sequenceRole} onChange={(event) => setSequenceRole(event.target.value)}>
            <option value="">{isItalian ? 'Qualsiasi ruolo sequenza' : 'Any sequence role'}</option>
            <option value="upstream_candidate">{isItalian ? 'Contributore a monte' : 'Upstream candidate'}</option>
            <option value="first_visible_failure">{isItalian ? 'Primo guasto visibile' : 'First visible failure'}</option>
            <option value="downstream_possible">{isItalian ? 'Possibile sintomo a valle' : 'Possible downstream symptom'}</option>
          </select>
          <select className="lab-input" value={clusterId} onChange={(event) => setClusterId(event.target.value)}>
            <option value="">{isItalian ? 'Qualsiasi famiglia di guasto' : 'Any failure family'}</option>
            {clusters.slice(0, 40).map((cluster) => {
              const value = String(cluster.session_cluster_id ?? cluster.cluster_id ?? '');
              return value ? <option key={value} value={value}>{value}</option> : null;
            })}
          </select>
          <label className="lab-checkbox">
            <input type="checkbox" checked={hideAmbiguousBurnLinks} onChange={(event) => setHideAmbiguousBurnLinks(event.target.checked)} />
            {isItalian ? 'Nascondi link burn-in ambigui' : 'Hide ambiguous burn-in links'}
          </label>
          <label className="lab-checkbox">
            <input type="checkbox" checked={strictOnly} onChange={(event) => setStrictOnly(event.target.checked)} />
            {isItalian ? 'Solo strict' : 'Strict only'}
          </label>
        </LabFilterBar>
        {error ? <p className="lab-error-text">{error}</p> : null}
        <div className="lab-metric-grid">
          <LabMetric label={isItalian ? 'Sospetti ricorrenti' : 'Recurring suspects'} value={summary.length} />
          <LabMetric label={isItalian ? 'Priorità massima' : 'Top priority'} value={summary[0]?.investigation_priority != null ? Number(summary[0].investigation_priority).toFixed(3) : '—'} help={{ ...smartiLabHelpCopy.evidenceScore, ariaLabel: 'Spiegazione punteggio di evidenza / Evidence score explanation' }} />
          <LabMetric label={isItalian ? 'Confidenza massima' : 'Top confidence'} value={summary[0]?.suspect_confidence != null ? Number(summary[0].suspect_confidence).toFixed(3) : '—'} help={{ ...smartiLabHelpCopy.suspectConfidence, ariaLabel: 'Spiegazione confidenza / Confidence explanation' }} />
          <LabMetric label={isItalian ? 'Avvisi weak-label' : 'Weak-label warnings'} value={runReport?.qualityWarnings?.length ?? 0} help={{ ...smartiLabHelpCopy.weakBaselineModel, ariaLabel: 'Spiegazione modello weak-label / Weak-label model explanation' }} />
        </div>
      </LabCard>

      <LabCard
        title={isItalian ? 'Sospetti ricorrenti' : 'Recurring suspects'}
        description={<LabBilingualCopy it="Mostra i passaggi che continuano a riapparire come sospetti con i filtri attuali. Il ranking combina evidenza, supporto e qualità del contesto." en="Shows the steps that keep reappearing as suspects with the current filters. The ranking combines evidence, support, and context quality." />}
        help={{ ...smartiLabHelpCopy.strictSuspects, ariaLabel: 'Spiegazione sospetti strict / Strict suspects explanation' }}
      >
        {summary.length ? (
          <div className="lab-suspect-grid">
            {summary.slice(0, 24).map((row, index) => (
              <article key={`${row.stepname || row.step_number_norm}-${index}`} className="lab-suspect-card">
                <div className="lab-suspect-head">
                  <div className="lab-data-stack">
                    <span className="lab-badge">{row.step_number_norm || 'Step'}</span>
                    <strong>{row.stepname || (isItalian ? 'Dato non disponibile' : 'Data not available')}</strong>
                    <p>{[row.station_name_norm, row.testspec_norm].filter(Boolean).join(' • ') || (isItalian ? 'Contesto non disponibile' : 'Context not available')}</p>
                  </div>
                  <div className="lab-chip-row">
                    {strictOnly ? <SmartiLabBadge variant="strict" label="Strict" /> : null}
                    <SmartiLabBadge variant={row.ambiguous_link_rows > 0 ? 'warning' : 'primary'} label={row.ambiguous_link_rows > 0 ? (isItalian ? 'Link ambiguo' : 'Ambiguous link') : (isItalian ? 'Link stabile' : 'Stable link')} />
                    {row.sequence_role ? (
                      <SmartiLabBadge
                        variant={row.sequence_role === 'downstream_possible' ? 'downstream' : row.sequence_role === 'first_visible_failure' ? 'firstFailure' : 'upstream'}
                        label={
                          row.sequence_role === 'downstream_possible'
                            ? (isItalian ? 'Sintomo a valle' : 'Downstream possible')
                            : row.sequence_role === 'first_visible_failure'
                              ? (isItalian ? 'Primo guasto' : 'First visible failure')
                              : (isItalian ? 'A monte' : 'Upstream')
                        }
                      />
                    ) : null}
                  </div>
                </div>
                <div className="lab-score-grid">
                  <SmartiLabScoreBar label={isItalian ? 'Punteggio evidenza' : 'Evidence score'} value={row.investigation_priority} />
                  <SmartiLabScoreBar label={isItalian ? 'Confidenza' : 'Confidence'} value={row.suspect_confidence} helper={`${isItalian ? 'Supporto' : 'Support'}: ${row.candidate_rows ?? 0}`} />
                  <SmartiLabScoreBar label={isItalian ? 'Score RF' : 'RF score'} value={row.rf_mean_candidate_score} helper={isItalian ? 'Ranking debole supportato dal modello.' : 'Model-supported weak ranking.'} />
                  <SmartiLabScoreBar label={isItalian ? 'Accordo modelli' : 'Model agreement'} value={row.model_agreement_score} helper={isItalian ? 'Quanto RF e motore forense si muovono insieme.' : 'How closely RF and the forensic engine agree.'} />
                </div>
                <div className="lab-chip-row">
                  <SmartiLabBadge variant="evidence" label={`${isItalian ? 'Fail' : 'Fail'}: ${row.fail_rows ?? 0}`} />
                  <SmartiLabBadge variant="warning" label={`${isItalian ? 'Near-limit' : 'Near-limit'}: ${row.near_limit_rows ?? 0}`} />
                  {row.pass_twin_delta_evidence != null ? <SmartiLabBadge variant="primary" label={`${isItalian ? 'Pass-twin' : 'Pass twin'} ${Number(row.pass_twin_delta_evidence).toFixed(2)}`} /> : null}
                  {row.time_drift_evidence != null ? <SmartiLabBadge variant="warning" label={`${isItalian ? 'Drift' : 'Drift'} ${Number(row.time_drift_evidence).toFixed(2)}`} /> : null}
                  {row.station_name_norm ? <SmartiLabBadge variant="neutral" label={row.station_name_norm} /> : null}
                </div>
                <div className="lab-suspect-reasons">
                  {formatList(row.top_evidence_reasons || row.top_evidence_reasons_json, isItalian ? 'Motivi dettagliati non disponibili.' : 'Detailed reasons not available.').slice(0, 3).map((reason) => (
                    <p key={reason} className="lab-helper-text">{reason}</p>
                  ))}
                </div>
                <div className="lab-action-row">
                  {row.top_candidate_id || row.candidate_id ? (
                    <Link className="lab-inline-button" to={`/smarti-lab/suspects/${encodeURIComponent(row.top_candidate_id || row.candidate_id)}`}>
                      {isItalian ? 'Apri evidenze' : 'Open evidence'}
                    </Link>
                  ) : null}
                  {row.top_candidate_id || row.candidate_id ? (
                    <Link className="lab-inline-button" to={`/smarti-lab/compare?mode=suspect&left=${encodeURIComponent(row.top_candidate_id || row.candidate_id)}`}>
                      {isItalian ? 'Confronta' : 'Compare'}
                    </Link>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <LabEmptyState title={noSuspectsCopy.title} description={noSuspectsCopy.body} />
        )}
      </LabCard>

      <div className="lab-grid two-col">
        <LabCard
          title={isItalian ? 'Segnali sequenziali a monte' : 'Upstream sequence signals'}
          description={<LabBilingualCopy it="Questi profili mostrano quali passaggi tendono a comparire prima del primo guasto visibile invece che dopo. Servono a separare possibili contributori a monte da sintomi a valle." en="These profiles show which steps tend to appear before the first visible failure instead of after it. They help separate possible upstream contributors from downstream symptoms." />}
          help={{ ...smartiLabHelpCopy.upstreamContributor, ariaLabel: 'Spiegazione contributore a monte / Upstream contributor explanation' }}
        >
          <LabTable
            rows={sequentialProfiles}
            emptyTitle={isItalian ? 'Nessun profilo sequenziale disponibile' : 'No sequence profile available'}
            emptyDescription={isItalian ? 'Servono sessioni fail con abbastanza storico step prima che i pattern a monte si stabilizzino.' : 'Failed sessions with enough step history are needed before upstream patterns stabilize.'}
            columns={[
              { key: 'step_name_norm', label: 'Step' },
              { key: 'upstream_score', label: isItalian ? 'Score a monte' : 'Upstream score' },
              { key: 'first_fail_rate', label: isItalian ? 'Tasso primo fail' : 'First-fail rate' },
              { key: 'pre_fail_presence_rate', label: isItalian ? 'Presenza pre-fail' : 'Pre-fail presence' },
              { key: 'downstream_symptom_penalty', label: isItalian ? 'Penalità a valle' : 'Downstream penalty' },
              { key: 'support_count', label: isItalian ? 'Supporto' : 'Support' },
            ]}
          />
        </LabCard>
        <LabCard
          title={isItalian ? 'Famiglie di guasto' : 'Failure families'}
          description={<LabBilingualCopy it="I cluster raggruppano sessioni con segnali simili: stessi step sospetti, sequenze vicine e contesto simile. Aiutano a capire se il problema appartiene a un pattern già visto." en="Clusters group sessions with similar signals: the same suspect steps, similar sequences, and similar context. They help show whether the problem belongs to a pattern already seen." />}
          help={{ ...smartiLabHelpCopy.failureFamilies, ariaLabel: 'Spiegazione famiglie di guasto / Failure families explanation' }}
        >
          <LabTable
            rows={clusters}
            emptyTitle={isItalian ? 'Nessuna famiglia di guasto disponibile' : 'No failure family available'}
            emptyDescription={isItalian ? 'Il clustering ha bisogno di somiglianze ripetute tra sessioni per formare gruppi stabili.' : 'Clustering needs repeated similarity across sessions to form stable groups.'}
            columns={[
              { key: 'session_cluster_id', label: 'Cluster' },
              { key: 'cluster_size', label: isItalian ? 'Dimensione' : 'Size' },
              { key: 'cluster_failure_rate', label: isItalian ? 'Tasso fail' : 'Fail rate' },
              { key: 'cluster_confidence', label: isItalian ? 'Confidenza' : 'Confidence' },
              { key: 'cluster_top_steps_list', label: isItalian ? 'Step principali' : 'Top steps', render: (value) => (value || []).join(', ') || '—' },
            ]}
          />
        </LabCard>
      </div>
        </div>
      </details>
    </div>
  );
}
